import { api } from './api.ts';
import { fromBase64, toBase64 } from './crypto/primitives.ts';
import { wrapDekFor, type KeyAlgorithm } from './crypto/keypair.ts';
import type { OpenedDocument } from './documents.ts';

/**
 * Dienste mit eigenem Schlüsselpaar – zuerst der Vertragsverwalter der KF Zentrale
 * (Karsten, 12.10.2026). Freigegeben wird je Ordner: Dieser Browser verpackt die
 * Dokumentschlüssel des Ordners (samt Unterordnern) zusätzlich für den öffentlichen
 * Schlüssel des Dienstes. Andere Dokumente erreicht der Dienst nie; die Freigabe lässt sich
 * zurücknehmen, und künftige Ablagen in dem Ordner werden automatisch mitverpackt.
 */
export interface Dienst {
  id: string;
  kennung: string;
  name: string;
  beschreibung: string | null;
  algorithm: KeyAlgorithm;
  publicKey: string;
  /** direkt freigegebene Ordner dieses Kontos */
  ordner: string[];
}
interface OrdnerKurz { id: string; parent_id: string | null }

let liste: Dienst[] | null = null;

/** Dienste, für die man freigeben kann (ohne Fehler, wenn es keine gibt). */
export async function dienste(neu = false): Promise<Dienst[]> {
  if (liste && !neu) return liste;
  try {
    liste = (await api.get<{ dienste: Dienst[] }>('/api/dienste')).dienste;
  } catch {
    liste = [];
  }
  return liste;
}
export function geladeneDienste(): Dienst[] {
  return liste ?? [];
}

/** Ist der Ordner (direkt oder über einen Elternordner) für den Dienst freigegeben? */
export function freigabeFuer(dienst: Dienst, folderId: string | null, ordner: OrdnerKurz[]): 'direkt' | 'geerbt' | null {
  if (!folderId) return null;
  if (dienst.ordner.includes(folderId)) return 'direkt';
  let aktuell = ordner.find((o) => o.id === folderId)?.parent_id ?? null;
  const gesehen = new Set<string>();
  while (aktuell && !gesehen.has(aktuell)) {
    if (dienst.ordner.includes(aktuell)) return 'geerbt';
    gesehen.add(aktuell);
    aktuell = ordner.find((o) => o.id === aktuell)?.parent_id ?? null;
  }
  return null;
}

function unterbaum(folderId: string, ordner: OrdnerKurz[]): Set<string> {
  const ids = new Set([folderId]);
  let gewachsen = true;
  while (gewachsen) {
    gewachsen = false;
    for (const o of ordner) if (o.parent_id && ids.has(o.parent_id) && !ids.has(o.id)) { ids.add(o.id); gewachsen = true; }
  }
  return ids;
}

/** Ordner freigeben: alle (bereits entschlüsselten) Dokumente darin für den Dienst verpacken. */
export async function ordnerFreigeben(dienst: Dienst, folderId: string, dokumente: OpenedDocument[], ordner: OrdnerKurz[]): Promise<number> {
  const ids = unterbaum(folderId, ordner);
  const schluessel = fromBase64(dienst.publicKey);
  const wraps = [];
  for (const d of dokumente.filter((x) => x.meta.folder_id && ids.has(x.meta.folder_id))) {
    wraps.push({ documentId: d.id, wrappedDek: toBase64(await wrapDekFor(d.dek, schluessel, dienst.algorithm)) });
  }
  const r = await api.post<{ dokumente: number }>(`/api/dienste/${dienst.id}/ordner/${folderId}`, { wraps });
  await dienste(true);
  return r.dokumente;
}

export async function freigabeAufheben(dienst: Dienst, folderId: string): Promise<void> {
  await api.delete(`/api/dienste/${dienst.id}/ordner/${folderId}`);
  await dienste(true);
}

/**
 * Nach dem Ablegen oder Verschieben: Liegt das Dokument in einem freigegebenen Ordner,
 * bekommt der Dienst seinen Umschlag. Fehler bremsen das Ablegen nie.
 */
export async function nachAblage(documentId: string, folderId: string | null, dek: CryptoKey, ordner?: OrdnerKurz[]): Promise<void> {
  if (!folderId) return;
  try {
    const alle = await dienste();
    if (!alle.length) return;
    const baum = ordner ?? (await api.get<{ folders: OrdnerKurz[] }>('/api/folders')).folders;
    for (const d of alle) {
      if (!freigabeFuer(d, folderId, baum)) continue;
      const wrappedDek = toBase64(await wrapDekFor(dek, fromBase64(d.publicKey), d.algorithm));
      await api.post(`/api/dienste/${d.id}/wraps`, { documentId, wrappedDek });
    }
  } catch {
    /* Ablage bleibt gültig; „Ordner erneut freigeben" holt fehlende Umschläge nach */
  }
}
